import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Flame, Award, Trophy, Target, Zap, BookOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  userId?: string;
  completedLessons: number;
  enrolledCourses: number;
}

interface StreakData {
  current: number;
  longest: number;
  lastVisit: string | null;
}

const todayStr = () => new Date().toISOString().slice(0, 10);
const yesterdayStr = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

const StreaksAndBadges = ({ userId, completedLessons, enrolledCourses }: Props) => {
  const [streak, setStreak] = useState<StreakData>({ current: 0, longest: 0, lastVisit: null });

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const sync = async () => {
      const { data } = await (supabase as any)
        .from("user_streaks")
        .select("current_streak, longest_streak, last_visit_date")
        .eq("user_id", userId)
        .maybeSingle();

      const today = todayStr();
      const existing: StreakData = data
        ? { current: data.current_streak, longest: data.longest_streak, lastVisit: data.last_visit_date }
        : { current: 0, longest: 0, lastVisit: null };

      if (existing.lastVisit === today) {
        if (!cancelled) setStreak(existing);
        return;
      }

      const next: StreakData = {
        current: existing.lastVisit === yesterdayStr() ? existing.current + 1 : 1,
        longest: 0,
        lastVisit: today,
      };
      next.longest = Math.max(next.current, existing.longest);

      await (supabase as any).from("user_streaks").upsert(
        {
          user_id: userId,
          current_streak: next.current,
          longest_streak: next.longest,
          last_visit_date: today,
        },
        { onConflict: "user_id" }
      );

      if (!cancelled) setStreak(next);
    };

    sync();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const badges = [
    { id: "first_step", label: "First Step", icon: BookOpen, earned: enrolledCourses >= 1, desc: "Enrolled in 1 course" },
    { id: "learner", label: "Learner", icon: Target, earned: completedLessons >= 1, desc: "Completed 1 lesson" },
    { id: "dedicated", label: "Dedicated", icon: Zap, earned: completedLessons >= 5, desc: "Completed 5 lessons" },
    { id: "scholar", label: "Scholar", icon: Award, earned: completedLessons >= 10, desc: "Completed 10 lessons" },
    { id: "champion", label: "Champion", icon: Trophy, earned: streak.current >= 7, desc: "7-day streak" },
  ];

  const earnedCount = badges.filter((b) => b.earned).length;

  return (
    <Card className="mb-6 overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-amber-500/5 to-transparent">
      <CardContent className="p-5">
        <div className="grid gap-4 md:grid-cols-[auto_1fr] items-center">
          <div className="flex items-center gap-3 md:pr-5 md:border-r md:border-border">
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
                <Flame className="w-7 h-7 text-white" />
              </div>
              {streak.current > 0 && (
                <span className="absolute -bottom-1 -right-1 bg-background border-2 border-orange-500 text-orange-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {streak.current}
                </span>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Daily Streak</p>
              <p className="text-2xl font-bold leading-tight">{streak.current} {streak.current === 1 ? "day" : "days"}</p>
              <p className="text-xs text-muted-foreground">Best: {streak.longest}</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Achievements</p>
              <Badge variant="secondary" className="text-xs">{earnedCount}/{badges.length}</Badge>
            </div>
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => {
                const Icon = b.icon;
                return (
                  <div
                    key={b.id}
                    title={`${b.label} — ${b.desc}`}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-all ${
                      b.earned
                        ? "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400"
                        : "bg-muted/30 border-border text-muted-foreground opacity-60"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StreaksAndBadges;
